import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';

const CRM_BASE_URL = 'https://crm.withings.com/public/api';// Use https://crm.corp.withings.com/public/api only for tests
const CRM_ID_PATTERN = /^[A-Za-z0-9_.-]+$/;
const CRM_KEY_MIN_LENGTH = 3;
const CRM_VALUE_MAX_LENGTH = 32;

/**
 * @typedef {Object} AxKmiFormRefs
 * @property {HTMLElement} emailStep
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} emailInput
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} successMessage
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} [legalSection]
 * @property {HTMLElement} [productStep]
 * @property {HTMLElement} [productStepHeading]
 * @property {HTMLFormElement} [productForm]
 * @property {HTMLButtonElement} [productSubmitButton]
 */

/**
 * @typedef {Object} CrmRequest
 * @property {string} url
 * @property {Record<string, unknown>} body
 */

/**
 * Post KMI form data to CRM: webhook for known users (`id_crm` + `id_wh` in URL), prospects otherwise.
 * Optional product step sends the checked cards' CRM properties.
 *
 * @extends {Component<AxKmiFormRefs>}
 */
class AxKmiFormComponent extends Component {
  requiredRefs = ['emailStep', 'form', 'emailInput', 'submitButton', 'successMessage', 'errorMessage'];

  /** @type {AbortController | null} */
  #abortController = null;

  #knownUserSent = false;

  connectedCallback() {
    super.connectedCallback();

    if (window.Shopify?.designMode) return;
    
    if (!this.#isKnownUser()) return;

    if (this.refs.productStep) {
      this.#showProductStep(false);
      return;
    }

    this.#sendKnownUser();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const { emailInput, submitButton, productStep } = this.refs;

    this.#resetMessages();
    emailInput.removeAttribute('aria-invalid');
    emailInput.removeAttribute('aria-describedby');
    submitButton.disabled = true;

    try {
      await this.#send();

      if (productStep) {
        this.#showProductStep(true);
      } else {
        this.#showSuccess(true);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      console.error('CRM submission failed:', error);
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.setAttribute('aria-describedby', this.refs.errorMessage.id);
      this.#showError();
    } finally {
      submitButton.disabled = false;
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleProductSubmit(event) {
    event.preventDefault();

    const { productForm, productSubmitButton } = this.refs;
    if (!productForm) return;

    const selection = this.#collectSelection(productForm);
    const hasSelection = Object.keys(selection).length > 0;

    if (!hasSelection && !this.#isKnownUser()) {
      this.#showSuccess(true);
      return;
    }

    this.#resetMessages();
    if (productSubmitButton) productSubmitButton.disabled = true;

    try {
      await this.#send(selection);
      this.#showSuccess(true);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      console.error('CRM product step submission failed:', error);
      this.#showError();
    } finally {
      if (productSubmitButton) productSubmitButton.disabled = false;
    }
  }

  /**
   * Known user without product step: post the topic to the webhook on load, then show success.
   * @returns {Promise<void>}
   */
  async #sendKnownUser() {
    if (this.#knownUserSent) return;
    this.#knownUserSent = true;

    try {
      await this.#send();
      this.#showSuccess(true);
    } catch (error) {
      this.#knownUserSent = false;
      if (error instanceof Error && error.name === 'AbortError') return;

      console.error('CRM known user submission failed:', error);
      this.#showError();
    }
  }

  /**
   * Send a request to the CRM, throws on HTTP or CRM error.
   * @param {Record<string, string>} [extraProperties]
   * @returns {Promise<void>}
   */
  async #send(extraProperties = {}) {
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    const { url, body } = this.#resolveRequest(extraProperties);
    const config = fetchConfig('json', { body: JSON.stringify(body) });
    const response = await fetch(url, { ...config, signal: abortController.signal });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    if (data?.status !== 0) throw new Error(`CRM status ${data?.status}`);
  }

  /**
   * Pick the CRM endpoint and payload from the URL params.
   * @param {Record<string, string>} extraProperties
   * @returns {CrmRequest}
   */
  #resolveRequest(extraProperties) {
    const params = new URLSearchParams(window.location.search);
    const properties = { ...this.#buildProperties(), ...extraProperties };

    if (this.#isKnownUser()) {
      const idWh = params.get('id_wh') || '';
      const testSuffix = params.get('crm_test') === '1' ? '/test' : '';

      return {
        url: `${CRM_BASE_URL}/webhook/${encodeURIComponent(idWh)}${testSuffix}`,
        body: { id_crm: params.get('id_crm'), ...properties },
      };
    }

    return {
      url: `${CRM_BASE_URL}/prospect`,
      body: {
        email: this.refs.emailInput.value.trim(),
        lang: (this.dataset.locale || '').slice(0, 2).toLowerCase(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
        properties,
      },
    };
  }

  /**
   * @returns {boolean} True when `id_crm` and `id_wh` are valid URL params and `autotopic` matches this form's topic
   */
  #isKnownUser() {
    const params = new URLSearchParams(window.location.search);
    const topic = this.#normalize(this.dataset.topic);

    return (
      CRM_ID_PATTERN.test(params.get('id_crm') || '') &&
      CRM_ID_PATTERN.test(params.get('id_wh') || '') &&
      topic !== '' &&
      topic === this.#normalize(params.get('autotopic') || '')
    );
  }

  /**
   * Build CRM properties, dropping empty values.
   * @returns {Record<string, string>}
   */
  #buildProperties() {
    const entries = {
      [this.#normalize(this.dataset.topic)]: '1',
      optin: '1',
      country: this.#normalize(this.dataset.country),
      store: this.#normalize(this.dataset.market),
    };

    return Object.fromEntries(Object.entries(entries).filter(([, value]) => value));
  }

  /**
   * Map checked product cards to `{ crm_property: '1' }`.
   * @param {HTMLFormElement} productForm
   * @returns {Record<string, string>}
   */
  #collectSelection(productForm) {
    /** @type {Record<string, string>} */
    const selection = {};

    for (const key of new FormData(productForm).keys()) {
      const normalized = this.#normalize(key);
      if (normalized.length >= CRM_KEY_MIN_LENGTH) selection[normalized] = '1';
    }

    return selection;
  }

  /**
   * Normalize a value to the CRM format `[a-z0-9_]{1,32}`.
   * @param {string | undefined} value
   * @returns {string}
   */
  #normalize(value) {
    return (value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .slice(0, CRM_VALUE_MAX_LENGTH);
  }

  /**
   * Swap the email form for the product step.
   * @param {boolean} moveFocus
   */
  #showProductStep(moveFocus) {
    const { emailStep, productStep, productStepHeading } = this.refs;
    if (!productStep) return;

    emailStep.hidden = true;
    productStep.hidden = false;
    document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));

    if (moveFocus) (productStepHeading ?? productStep).focus();
  }

  /**
   * @param {boolean} moveFocus
   */
  #showSuccess(moveFocus) {
    const { emailStep, productStep, successMessage, legalSection } = this.refs;

    emailStep.hidden = true;
    if (legalSection) legalSection.style.display = 'none';
    if (productStep) productStep.hidden = true;
    successMessage.hidden = false;
    if (moveFocus) successMessage.focus();
  }

  #showError() {
    const { errorMessage } = this.refs;

    errorMessage.hidden = false;
    errorMessage.focus();
  }

  #resetMessages() {
    this.refs.successMessage.hidden = true;
    if (this.refs.legalSection) this.refs.legalSection.style.display = 'flex';
    this.refs.errorMessage.hidden = true;
  }
}

if (!customElements.get('ax-kmi-form-component')) {
  customElements.define('ax-kmi-form-component', AxKmiFormComponent);
}
